import React, { useEffect, useMemo, useState } from 'react';
import { roastRecords, coffeeDispatches } from '../lib/api';
import { AlertTriangle, Download, Info } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { inr, rs } from '../lib/format';
import { downloadPdfReport } from '../lib/pdfReport';

const PRESETS = [
  { k: 'all', label: 'All Time' },
  { k: 'today', label: 'Today' },
  { k: 'yesterday', label: 'Yesterday' },
  { k: 'last_7', label: 'Last 7 Days' },
  { k: 'this_month', label: 'This Month' },
  { k: 'last_month', label: 'Last Month' },
  { k: 'custom', label: 'Custom Range' },
];

function computeRange(preset, customFrom, customTo) {
  const now = new Date();
  switch (preset) {
    case 'today': return { from: startOfDay(now), to: endOfDay(now), label: 'Today' };
    case 'yesterday': { const y = subDays(now, 1); return { from: startOfDay(y), to: endOfDay(y), label: 'Yesterday' }; }
    case 'last_7': return { from: startOfDay(subDays(now, 6)), to: endOfDay(now), label: 'Last 7 Days' };
    case 'this_month': return { from: startOfMonth(now), to: endOfMonth(now), label: 'This Month' };
    case 'last_month': { const lm = subMonths(now, 1); return { from: startOfMonth(lm), to: endOfMonth(lm), label: 'Last Month' }; }
    case 'custom':
      if (!customFrom || !customTo) return { from: null, to: null, label: 'Custom' };
      return { from: startOfDay(new Date(customFrom)), to: endOfDay(new Date(customTo)), label: `${customFrom} to ${customTo}` };
    default: return { from: null, to: null, label: 'All Time' };
  }
}

const COLORS = ['#ef4444', '#f97316', '#eab308', '#10b981', '#06b6d4', '#8b5cf6', '#ec4899'];

const Wastage = () => {
  const [records, setRecords] = useState([]);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [r, k] = await Promise.all([roastRecords.getAll(), coffeeDispatches.getCosts()]);
        setRecords(r.data);
        setCosts(k.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const range = useMemo(() => computeRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const filtered = useMemo(() => {
    if (!range.from || !range.to) return records;
    return records.filter((r) => {
      const d = new Date(r.roast_date);
      return d >= range.from && d <= range.to;
    });
  }, [records, range]);

  const totalWastage = filtered.reduce((s, r) => s + r.wastage_kg, 0);
  const totalGreen = filtered.reduce((s, r) => s + r.green_input_kg, 0);
  const avgShrinkage = totalGreen > 0 ? ((totalWastage / totalGreen) * 100).toFixed(2) : '0.00';
  const greenCostFor = (catId) => costs.find((c) => c.category_id === catId)?.green_cost_per_kg || 0;
  const totalWastageCost = filtered.reduce((s, r) => s + r.wastage_kg * greenCostFor(r.category_id), 0);

  // Aggregate by category
  const byCategory = {};
  filtered.forEach((r) => {
    const a = byCategory[r.category_name] || { kg: 0, cost: 0 };
    a.kg += r.wastage_kg;
    a.cost += r.wastage_kg * greenCostFor(r.category_id);
    byCategory[r.category_name] = a;
  });
  const catChart = Object.entries(byCategory)
    .map(([name, v]) => ({ name, value: Number(v.kg.toFixed(2)), cost: Number(v.cost.toFixed(2)) }))
    .sort((a, b) => b.value - a.value);

  const handleDownloadPdf = () => {
    downloadPdfReport({
      title: 'Wastage Report',
      subtitle: range.label,
      summary: [
        { label: 'Total Wastage', value: `${totalWastage.toFixed(2)} kg` },
        { label: 'Wastage Cost', value: rs(totalWastageCost) },
        { label: 'Green Input', value: `${totalGreen.toFixed(2)} kg` },
        { label: 'Avg Shrinkage', value: `${avgShrinkage}%` },
        { label: 'Roast Count', value: filtered.length },
      ],
      tables: [
        {
          title: 'Wastage by Category',
          head: ['Category', 'Wastage (kg)', 'Wastage Cost'],
          rows: catChart.map((c) => [c.name, c.value.toFixed(2), rs(c.cost)]),
        },
        {
          title: 'Roast-Level Wastage Records',
          head: ['Date', 'Category', 'Roast Level', 'Green (kg)', 'Roasted (kg)', 'Wastage (kg)', 'Shrinkage %'],
          rows: filtered.map((r) => [
            format(new Date(r.roast_date), 'yyyy-MM-dd'),
            r.category_name,
            r.roast_level,
            r.green_input_kg.toFixed(2),
            r.roasted_output_kg.toFixed(2),
            r.wastage_kg.toFixed(2),
            r.green_input_kg > 0 ? `${((r.wastage_kg / r.green_input_kg) * 100).toFixed(1)}%` : '-',
          ]),
        },
      ],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wastage-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Wastage</h1>
          <p className="text-gray-600 mt-1">Auto-calculated from each roast (green input − roasted output)</p>
        </div>
        <button onClick={handleDownloadPdf} data-testid="download-wastage-pdf"
          className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow">
          <Download className="w-4 h-4" /><span>Download PDF</span>
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-900">
          Wastage is recorded automatically every time you log a roast — equal to the difference between green input and roasted output.
        </p>
      </div>

      {/* Date preset */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.k} onClick={() => setPreset(p.k)} data-testid={`wastage-preset-${p.k}`}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition ${preset === p.k ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex gap-3 pt-2 border-t">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="px-3 py-2 border rounded-lg" />
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="px-3 py-2 border rounded-lg" />
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Total Wastage</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{totalWastage.toFixed(2)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Wastage Cost</p>
          <p className="text-2xl font-bold text-rose-600 mt-1" data-testid="total-wastage-cost">{inr(totalWastageCost)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Green Input</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{totalGreen.toFixed(2)} kg</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Avg Shrinkage</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{avgShrinkage}%</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase">Roast Count</p>
          <p className="text-2xl font-bold text-gray-800 mt-1">{filtered.length}</p>
        </div>
      </div>

      {/* Wastage by category chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Wastage by Category</h3>
        {catChart.length === 0 ? (
          <p className="text-center text-gray-400 py-8 text-sm">No wastage in this period.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={catChart} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" angle={-15} textAnchor="end" height={50} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${v}kg`} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v, name, props) => [`${Number(v).toFixed(2)} kg (${inr(props.payload.cost)})`, 'Wastage']} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {catChart.map((entry, idx) => (
                  <Cell key={entry.name} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Detail table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <h3 className="text-lg font-bold text-gray-800">Wastage Records</h3>
        </div>
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No records.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Category</th>
                  <th className="text-left py-3 px-6 text-sm font-semibold text-gray-700">Roast Level</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Green (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Roasted (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Wastage (kg)</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Wastage ₹</th>
                  <th className="text-right py-3 px-6 text-sm font-semibold text-gray-700">Shrinkage %</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-6 text-sm">{format(new Date(r.roast_date), 'MMM dd, yyyy')}</td>
                    <td className="py-3 px-6 font-medium">{r.category_name}</td>
                    <td className="py-3 px-6">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-semibold ${
                        r.roast_level === 'Light' ? 'bg-yellow-100 text-yellow-800' :
                        r.roast_level === 'Dark' ? 'bg-stone-700 text-white' :
                        'bg-amber-100 text-amber-800'
                      }`}>{r.roast_level}</span>
                    </td>
                    <td className="py-3 px-6 text-right">{r.green_input_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right">{r.roasted_output_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right font-bold text-red-600">{r.wastage_kg.toFixed(2)}</td>
                    <td className="py-3 px-6 text-right text-sm text-rose-600 font-semibold">
                      {greenCostFor(r.category_id) > 0 ? inr(r.wastage_kg * greenCostFor(r.category_id)) : '-'}
                    </td>
                    <td className="py-3 px-6 text-right text-amber-600 font-semibold">
                      {r.green_input_kg > 0 ? `${((r.wastage_kg / r.green_input_kg) * 100).toFixed(1)}%` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wastage;
